import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Leaf, Truck, Users, ShieldCheck, Thermometer, HandCoins,
  ArrowRight, CheckCircle, Star, MapPin, Clock, Sprout
} from 'lucide-react';
import SEO, { createBreadcrumbSchema } from '../../components/SEO';

const OrivanaFresh = () => {
  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Products', url: '/products' },
    { name: 'ORIVANA Fresh', url: '/products/orivana-fresh' }
  ]);

  const categories = [
    {
      name: 'Grapes',
      description: 'Premium table grapes - Sonaka, Black, Thompson & more',
      image: 'https://images.pexels.com/photos/708777/pexels-photo-708777.jpeg?auto=compress&cs=tinysrgb&w=800',
      link: '/products/orivana-fresh/grapes',
      count: '5 varieties'
    },
    {
      name: 'Mangoes',
      description: 'King of fruits - Alphonso, Kesar, Dasheri & more',
      image: 'https://images.pexels.com/photos/2294471/pexels-photo-2294471.jpeg?auto=compress&cs=tinysrgb&w=800',
      link: '/products/orivana-fresh/mangoes',
      count: '6 varieties'
    },
    {
      name: 'Pomegranates',
      description: 'Ruby red Bhagwa, Arakta & Ganesh varieties',
      image: 'https://images.pexels.com/photos/65256/pomegranate-open-cores-fruit-65256.jpeg?auto=compress&cs=tinysrgb&w=800',
      link: '/products/orivana-fresh/pomegranates',
      count: '5 varieties'
    },
    {
      name: 'Citrus Fruits',
      description: 'Nagpur Orange, Kinnow, Sweet Lime, Lemons & more',
      image: 'https://images.pexels.com/photos/1937743/pexels-photo-1937743.jpeg?auto=compress&cs=tinysrgb&w=800',
      link: '/products/orivana-fresh/citrus',
      count: '6 varieties'
    },
    {
      name: 'Onions',
      description: 'Red, White, Pink & Shallots - Export quality',
      image: 'https://images.pexels.com/photos/4197447/pexels-photo-4197447.jpeg?auto=compress&cs=tinysrgb&w=800',
      link: '/products/orivana-fresh/onions',
      count: '5 varieties'
    },
    {
      name: 'Vegetables',
      description: 'Farm fresh seasonal vegetables - 11+ varieties',
      image: 'https://images.pexels.com/photos/1656666/pexels-photo-1656666.jpeg?auto=compress&cs=tinysrgb&w=800',
      link: '/products/orivana-fresh/vegetables',
      count: '11 varieties'
    }
  ];

  const features = [
    {
      icon: Leaf,
      title: 'Farm Fresh Guarantee',
      description: 'Direct sourcing from verified farms ensures harvest-to-delivery freshness within 24-48 hours'
    },
    {
      icon: ShieldCheck,
      title: 'Quality Certified',
      description: 'Every batch undergoes rigorous quality checks. Grade A produce with complete traceability'
    },
    {
      icon: Truck,
      title: 'Cold Chain Logistics',
      description: 'Temperature-controlled transport maintains optimal freshness from farm to warehouse'
    },
    {
      icon: Users,
      title: 'B2B Focused',
      description: 'Tailored solutions for wholesalers, retailers, HORECA, and institutional buyers'
    },
    {
      icon: Thermometer,
      title: 'Optimal Storage',
      description: 'State-of-the-art cold storage facilities to extend shelf life and maintain quality'
    },
    {
      icon: HandCoins,
      title: 'Competitive Pricing',
      description: 'Direct farm partnerships eliminate middlemen, offering best prices for bulk orders'
    }
  ];

  const stats = [
    { value: '5000+', label: 'Partner Farmers' },
    { value: '500+', label: 'Tonnes Monthly' },
    { value: '100+', label: 'Cities Served' },
    { value: '98%', label: 'Quality Score' }
  ];

  const featuredFarmers = [
    {
      name: 'Ramesh Kisan Patil',
      location: 'Nashik, Maharashtra',
      specialty: 'Sonaka Grapes',
      image: 'https://images.unsplash.com/photo-1608366911553-3296e6f5e2ef?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
      story: 'Third-generation grape farmer. SPANXPORT helped us get GlobalGAP certified and now we export to Europe.'
    },
    {
      name: 'Shantabai Pawar',
      location: 'Junnar, Pune',
      specialty: 'Flame Grapes',
      image: 'https://images.unsplash.com/photo-1709207513502-d9242031ab1b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
      story: 'Woman farmer leading a collective of 15 women. Our organic grapes reach UAE markets during Christmas.'
    },
    {
      name: 'Ganesh Vishnu Sawant',
      location: 'Ratnagiri, Maharashtra',
      specialty: 'Alphonso Mangoes',
      image: 'https://images.pexels.com/photos/29912210/pexels-photo-29912210.jpeg?auto=compress&cs=tinysrgb&w=400',
      story: 'GI-tagged Alphonso farmer. Four generations of mango cultivation on coastal hills.'
    }
  ];

  return (
    <div className="w-full">
      <SEO 
        title="ORIVANA Fresh - Premium Indian Fresh Produce for B2B | SPANXPORT"
        description="Your single stop for premium fresh produce. Farm-fresh grapes, mangoes, pomegranates, citrus and vegetables. Quality & transparency assured, fair prices to farmers. Direct from India's finest farms."
        keywords="Indian grapes export, Alphonso mango, pomegranate export, fresh vegetables, B2B fresh produce, wholesale fruits India, ORIVANA Fresh"
        url="/products/orivana-fresh"
        structuredData={breadcrumbSchema}
      />
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1656666/pexels-photo-1656666.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Fresh produce"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#263776]/95 via-[#263776]/80 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl">
            <Badge className="bg-[#F47D20] text-white mb-4 text-sm px-4 py-1">
              ORIVANA Fresh
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Your Single Stop for Premium Fresh Produce
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-4 leading-relaxed">
              Farm-fresh grapes, mangoes, pomegranates, citrus and vegetables delivered directly to wholesalers and retailers.
            </p>
            <p className="text-lg text-[#F47D20] font-semibold mb-8">
              Quality & Transparency Assured. Fair Prices to Farmers.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-[#F47D20] hover:bg-[#d66b1a] text-white">
                <Link to="/products/orivana-fresh/enquiry">
                  Request Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/verified-directory/apply-farmer">
                  Become a Partner Farmer
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#263776] py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-[#F47D20]">{stat.value}</div>
                <div className="text-white/80 mt-1 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="bg-[#263776]/10 text-[#263776] mb-4">OUR PRODUCTS</Badge>
            <h2 className="text-4xl font-bold text-[#263776] mb-4">Fresh Produce Categories</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Premium quality fruits and vegetables sourced directly from verified farms across India
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, idx) => (
              <Link to={category.link} key={idx}>
                <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-300 h-full">
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <Badge className="absolute top-4 right-4 bg-[#F47D20] text-white">
                      {category.count}
                    </Badge>
                    <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">
                      {category.name}
                    </h3>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-slate-600">{category.description}</p>
                    <div className="flex items-center text-[#263776] font-medium mt-4 group-hover:text-[#F47D20] transition-colors">
                      View Products
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="bg-[#F47D20]/10 text-[#F47D20] mb-4">WHY ORIVANA FRESH</Badge>
            <h2 className="text-4xl font-bold text-[#263776] mb-4">The SPANXPORT Advantage</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              End-to-end supply chain excellence for your fresh produce needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <Card key={idx} className="p-6 hover:shadow-lg transition-shadow border-t-4 border-t-[#F47D20]">
                <feature.icon className="w-12 h-12 text-[#263776] mb-4" />
                <h3 className="text-xl font-bold text-[#263776] mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Our Farmers */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="bg-[#263776]/10 text-[#263776] mb-4">FARM STORIES</Badge>
            <h2 className="text-4xl font-bold text-[#263776] mb-4">Meet Our Farmers</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              The hardworking hands behind every fresh delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredFarmers.map((farmer, idx) => (
              <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative h-64">
                  <img
                    src={farmer.image}
                    alt={farmer.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white">{farmer.name}</h3>
                    <div className="flex items-center text-white/80 text-sm mt-1">
                      <MapPin className="w-4 h-4 mr-1" />
                      {farmer.location}
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <Badge className="bg-[#F47D20]/10 text-[#F47D20] mb-3">
                    {farmer.specialty}
                  </Badge>
                  <p className="text-slate-600">{farmer.story}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button asChild variant="outline" className="border-[#263776] text-[#263776] hover:bg-[#263776] hover:text-white">
              <Link to="/products/orivana-fresh/farmers">
                Become a Partner Farmer
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* For Wholesalers & Retailers */}
      <section className="py-20 bg-gradient-to-br from-[#263776] to-[#1e2d5f] text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#F47D20] text-white mb-4">FOR BUSINESS</Badge>
              <h2 className="text-4xl font-bold mb-6">Wholesale & Retail Solutions</h2>
              <p className="text-xl text-white/80 mb-8">
                Whether you're a wholesaler, retailer, restaurant, or institutional buyer, ORIVANA Fresh offers customized supply solutions for your business.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Bulk orders with competitive pricing',
                  'Flexible delivery schedules',
                  'Credit terms for regular buyers',
                  'Dedicated account manager',
                  'Real-time order tracking',
                  'Quality guarantee with replacement policy'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-[#F47D20] mr-3 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Button asChild size="lg" className="bg-[#F47D20] hover:bg-[#d66b1a]">
                <Link to="/products/orivana-fresh/enquiry">
                  Get Business Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/2252584/pexels-photo-2252584.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Wholesale market"
                className="rounded-xl shadow-2xl"
              />
              <Card className="absolute -bottom-6 -left-6 p-4 bg-white text-slate-900 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#F47D20]/10 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#F47D20]" />
                  </div>
                  <div>
                    <div className="font-bold text-[#263776]">24-48 Hour Delivery</div>
                    <div className="text-sm text-slate-500">Farm to your warehouse</div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-[#263776] mb-4">Ready to Source Premium Fresh Produce?</h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Connect with us for bulk orders, custom requirements, or partnership opportunities
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-[#F47D20] hover:bg-[#d66b1a]">
              <Link to="/products/orivana-fresh/enquiry">Request Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-[#263776] text-[#263776] hover:bg-[#263776] hover:text-white">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Partner Farmer CTA */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sprout className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-[#263776] mb-4">Are You a Farmer?</h2>
            <p className="text-xl text-slate-600 mb-6 max-w-2xl mx-auto">
              Join our Partner Farmer Network and get direct access to premium export markets. 
              Better prices, timely payments, and certification support.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {['Premium Prices', 'Direct Payments', 'Training Support', 'Certification Help'].map((benefit, idx) => (
                <Badge key={idx} className="bg-green-100 text-green-700 px-4 py-2">
                  <CheckCircle className="w-4 h-4 mr-2 inline" />
                  {benefit}
                </Badge>
              ))}
            </div>
            <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 text-white">
              <Link to="/verified-directory/apply-farmer">
                <Sprout className="w-5 h-5 mr-2" />
                Become a Partner Farmer
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OrivanaFresh;
